import { ListItem, ListItemText, SxProps } from "@mui/material";
import { HTMLAttributes, ReactNode } from "react";
import { CityAndCounty } from "../../../../types/apiTypes";
import AutocompleteBase from "../autocompleteBase/AutocompleteBase";
import { MultipleValueProps, SingleValueProps } from "./types";
import { cityId, useCityAndCountyOptions } from "./useCityAndCountyOptions";

type BaseProps = {
  multiple?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  limitTags?: number;
  groupByCounty?: boolean;
  allLabel?: string;
  isOptionEqualToValue?: (
    option: CityAndCounty,
    value: CityAndCounty,
  ) => boolean;
  getOptionLabel?: (option: CityAndCounty) => string;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: CityAndCounty,
  ) => ReactNode;
  sx?: SxProps;
};

type Props = BaseProps & (SingleValueProps | MultipleValueProps);

const CitiesAndCountiesAutocomplete = ({
  multiple,
  value,
  onChange,
  fullWidth = true,
  disabled,
  required,
  error,
  helperText,
  placeholder,
  groupByCounty = true,
  allLabel,
  isOptionEqualToValue,
  getOptionLabel,
  renderOption,
  limitTags,
  sx = {},
}: Props) => {
  const { options, isLoading } = useCityAndCountyOptions({
    allLabel,
    groupByCounty,
  });

  const isEqualToValue =
    isOptionEqualToValue ?? ((a, b) => cityId(a) === cityId(b));

  const getLabel =
    getOptionLabel ?? ((opt) => (typeof opt === "string" ? opt : opt.city));

  const renderOpt =
    renderOption ??
    ((p, opt) => (
      <ListItem {...p} key={cityId(opt)}>
        <ListItemText>{opt.city}</ListItemText>
      </ListItem>
    ));

  const groupBy = groupByCounty
    ? (opt: CityAndCounty) => opt.county
    : undefined;

  return (
    <AutocompleteBase<CityAndCounty>
      value={
        multiple
          ? (value as CityAndCounty[] | undefined) || []
          : (value as CityAndCounty | null)
            ? [(value as CityAndCounty)]
            : []
      }
      onChange={(newValue) => {
        if (multiple) {
          onChange(newValue as CityAndCounty[]);
        } else {
          onChange(
            (newValue && newValue.length > 0
              ? newValue[0]
              : null) as CityAndCounty | null,
          );
        }
      }}
      options={options}
      getOptionLabel={getLabel}
      isOptionEqualToValue={isEqualToValue}
      multiple={!!multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      limitTags={limitTags}
      loading={isLoading}
      required={required}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      groupBy={groupBy}
      renderOption={renderOpt}
      sx={sx}
    />
  );
};

export default CitiesAndCountiesAutocomplete;
